import { parseAmt } from './utils';

// ── GST Helpers ───────────────────────────────────────────────────────────────
// Extract 2-digit state code from GSTIN (first 2 chars) or from state name map
const STATE_CODES = {
  'Andhra Pradesh': '37', 'Arunachal Pradesh': '12', 'Assam': '18', 'Bihar': '10',
  'Chhattisgarh': '22', 'Goa': '30', 'Gujarat': '24', 'Haryana': '06',
  'Himachal Pradesh': '02', 'Jharkhand': '20', 'Karnataka': '29', 'Kerala': '32',
  'Madhya Pradesh': '23', 'Maharashtra': '27', 'Manipur': '14', 'Meghalaya': '17',
  'Mizoram': '15', 'Nagaland': '13', 'Odisha': '21', 'Punjab': '03',
  'Rajasthan': '08', 'Sikkim': '11', 'Tamil Nadu': '33', 'Telangana': '36',
  'Tripura': '16', 'Uttar Pradesh': '09', 'Uttarakhand': '05', 'West Bengal': '19',
  'Andaman and Nicobar Islands': '35', 'Chandigarh': '04', 'Dadra and Nagar Haveli and Daman and Diu': '26',
  'Delhi': '07', 'Jammu and Kashmir': '01', 'Ladakh': '38', 'Lakshadweep': '31',
  'Puducherry': '34'
};

function getStateCode(gstin, stateName) {
  if (gstin && gstin.length >= 2) return gstin.substring(0, 2);
  return STATE_CODES[stateName] || '';
}

// Returns 'IGST' if inter-state, 'CGST_SGST' if intra-state, null if can't determine
function detectGstType(companyGstin, companyState, partyGstin, partyState) {
  const companyCode = getStateCode(companyGstin, companyState);
  const partyCode   = getStateCode(partyGstin, partyState);
  if (!companyCode || !partyCode) return null;
  return companyCode === partyCode ? 'CGST_SGST' : 'IGST';
}

// ── Per-item GST calculation (supports mixed rates + CESS) ───────────────────
// Returns { cgst, sgst, igst, cess } based on each item's gstRate and cessRate
function calcPerItemGst(items, gstType, itemDb) {
  let totalCgst = 0, totalSgst = 0, totalIgst = 0, totalCess = 0;
  items.forEach(item => {
    if (!item.name?.value) return;
    const itemAmt = parseAmt(item.amount) || 0;
    if (itemAmt <= 0) return;
    const def      = itemDb?.[item.name.value];
    const gstRate  = def?.gstRate  ?? 0;
    const cessRate = def?.cessRate ?? 0;
    if (gstRate > 0) {
      const gstAmt = (itemAmt * gstRate) / 100;
      if (gstType === 'IGST') { totalIgst += gstAmt; }
      else { totalCgst += gstAmt / 2; totalSgst += gstAmt / 2; }
    }
    if (cessRate > 0) {
      totalCess += (itemAmt * cessRate) / 100;
    }
  });
  return {
    cgst: Math.round(totalCgst * 100) / 100,
    sgst: Math.round(totalSgst * 100) / 100,
    igst: Math.round(totalIgst * 100) / 100,
    cess: Math.round(totalCess * 100) / 100,
  };
}

// Build updated charge rows with correct per-item GST amounts
function buildAutoGstChargesPerItem(existingCharges, gstType, itemDb, items) {
  if (!gstType || !items || items.length === 0) return existingCharges;

  const { cgst, sgst, igst, cess } = calcPerItemGst(items, gstType, itemDb);
  const totalGst = cgst + sgst + igst;
  if (totalGst <= 0 && cess <= 0) return existingCharges;

  // Keep non-GST charges (Discount, Freight, Round Off, etc.)
  const nonGstCharges = existingCharges.filter(
    c => !['CGST', 'SGST', 'IGST', 'CESS'].includes(c.ledger?.value)
  );

  const result = [...nonGstCharges];
  if (gstType === 'IGST') {
    if (igst > 0) result.push({ ledger: { text: 'IGST', value: 'IGST' }, amount: igst.toFixed(2) });
  } else {
    if (cgst > 0) result.push({ ledger: { text: 'CGST', value: 'CGST' }, amount: cgst.toFixed(2) });
    if (sgst > 0) result.push({ ledger: { text: 'SGST', value: 'SGST' }, amount: sgst.toFixed(2) });
  }
  if (cess > 0) result.push({ ledger: { text: 'CESS', value: 'CESS' }, amount: cess.toFixed(2) });
  return result;
}

// Legacy single-rate helper kept for backward compat (called if itemDb not available)
function buildAutoGstCharges(existingCharges, taxableAmount, gstType, gstRatePercent) {
  if (!gstType || !gstRatePercent || taxableAmount <= 0) return existingCharges;

  const nonGstCharges = existingCharges.filter(
    c => !['CGST', 'SGST', 'IGST', 'CESS'].includes(c.ledger?.value)
  );

  if (gstType === 'IGST') {
    const igstAmt = ((taxableAmount * gstRatePercent) / 100).toFixed(2);
    return [...nonGstCharges, { ledger: { text: 'IGST', value: 'IGST' }, amount: igstAmt }];
  } else {
    const halfRate = gstRatePercent / 2;
    const halfAmt  = ((taxableAmount * halfRate) / 100).toFixed(2);
    return [
      ...nonGstCharges,
      { ledger: { text: 'CGST', value: 'CGST' }, amount: halfAmt },
      { ledger: { text: 'SGST', value: 'SGST' }, amount: halfAmt },
    ];
  }
}

// ── TaxEngine ─────────────────────────────────────────────────────────────────
export const TaxEngine = {
  calculateItemAmount: (qty, rate) => (qty * rate).toFixed(2),
  calculateTax: (amount, rate) => ((amount * rate) / 100).toFixed(2),
  calculateItemsTotal: (items) => items.reduce((sum, item) => sum + (parseAmt(item.amount) || 0), 0),
  calculateChargesTotal: (charges) => charges.filter(c => c.ledger?.value && parseAmt(c.amount) !== 0).reduce((sum, c) => sum + (parseAmt(c.amount) || 0), 0),
  calculateFinalAmount: (items, charges) => {
    const itemTotal = TaxEngine.calculateItemsTotal(items);
    let chargeTotal = 0;
    charges.forEach(charge => {
      const ledger = charge.ledger?.value;
      const amount = parseAmt(charge.amount) || 0;
      if (!ledger || amount === 0) return;
      if (ledger === 'Discount') chargeTotal -= Math.abs(amount);
      else chargeTotal += amount;
    });
    return itemTotal + chargeTotal;
  },

  // Called whenever items or charges change — recalculates existing GST rows
  // itemDb is optional; when provided, uses per-item rates (more accurate for mixed rates)
  recalculateTaxes: (formData, ledgerDb, itemDb) => {
    // Detect current GST type from existing charge rows
    const hasIgst  = formData.charges.some(c => c.ledger?.value === 'IGST');
    const hasCgst  = formData.charges.some(c => c.ledger?.value === 'CGST');
    const currentGstType = hasIgst ? 'IGST' : (hasCgst ? 'CGST_SGST' : null);

    // If itemDb available AND we know the GST type → use per-item calculation
    if (itemDb && currentGstType) {
      const items = formData.items;
      const newCharges = buildAutoGstChargesPerItem(formData.charges, currentGstType, itemDb, items);
      // Ensure trailing blank
      const hasBlank = newCharges.length === 0 || !newCharges[newCharges.length - 1].ledger?.value;
      return { ...formData, charges: hasBlank ? newCharges : [...newCharges, { ledger: { text: '', value: '' }, amount: '' }] };
    }

    // Fallback: legacy single-rate approach using ledgerDb taxRate
    let discount = 0;
    let freight  = 0;
    formData.charges.forEach(charge => {
      if (charge.ledger?.value === 'Discount') discount = Math.abs(parseAmt(charge.amount) || 0);
      if (charge.ledger?.value === 'Freight Charges') freight += parseAmt(charge.amount) || 0;
    });
    const taxableAmount = TaxEngine.calculateItemsTotal(formData.items) - discount + freight;

    const updatedCharges = formData.charges.map(c => {
      const info = ledgerDb[c.ledger?.value];
      if (info && info.taxRate && ['CGST', 'SGST', 'IGST', 'CESS'].includes(c.ledger?.value)) {
        return { ...c, amount: TaxEngine.calculateTax(taxableAmount, info.taxRate) };
      }
      return c;
    });
    return { ...formData, charges: updatedCharges };
  },

  // Called when party is selected or items change — auto-builds GST charge rows
  // companyInfo: { gstin, state }
  // partyLedgerInfo: from ledgerDb — { gstin, state, group }
  // items: current items array
  // existingCharges: current charges array
  // itemDb: for gstRate / cessRate lookup
  autoFillGst: (companyInfo, partyLedgerInfo, items, existingCharges, itemDb) => {
    if (!partyLedgerInfo || !items || items.length === 0) return null;

    // Determine GST type (inter vs intra-state)
    const gstType = detectGstType(
      companyInfo?.gstin, companyInfo?.state,
      partyLedgerInfo.gstin, partyLedgerInfo.state
    );
    if (!gstType) return null; // Can't determine — don't auto-fill

    // Check if any item has a GST rate
    const hasGstItems = items.some(item => {
      if (!item.name?.value) return false;
      const def = itemDb?.[item.name.value];
      return def?.gstRate > 0;
    });
    if (!hasGstItems) return null;

    // Use per-item calculation for accuracy with mixed rates
    const newCharges = buildAutoGstChargesPerItem(existingCharges, gstType, itemDb, items);
    // Always ensure trailing blank row
    const hasBlankEnd = newCharges.length === 0 || !newCharges[newCharges.length - 1].ledger?.value;
    return hasBlankEnd ? newCharges : [...newCharges, { ledger: { text: '', value: '' }, amount: '' }];
  },

  detectGstType,
};

// ── VoucherEngineLogic ────────────────────────────────────────────────────────
const JOURNAL_TYPES = new Set(['Journal', 'Contra', 'Payment', 'Receipt']);
const END_OF_LIST   = "End of List";

export const VoucherEngineLogic = {
  autoRoundOff: (formData) => {
    const finalAmt = TaxEngine.calculateFinalAmount(formData.items, formData.charges);
    const rounded = Math.round(finalAmt);
    const diff = +(rounded - finalAmt).toFixed(2);
    const charges = formData.charges.filter(c => c.ledger?.value !== 'Round Off');
    if (Math.abs(diff) >= 0.01) {
      charges.push({ ledger: { text: 'Round Off', value: 'Round Off' }, amount: String(diff) });
    }
    const hasBlank = charges.length === 0 || !charges[charges.length - 1].ledger?.value;
    return { ...formData, charges: hasBlank ? charges : [...charges, { ledger: { text: '', value: '' }, amount: '' }] };
  },

  processVoucher: (data, vType) => {
    let entries    = [];
    let totalDebit  = 0;
    let totalCredit = 0;

    if (JOURNAL_TYPES.has(vType)) {
      data.journalRows.forEach(row => {
        if (row.ledger.value && row.ledger.value !== END_OF_LIST) {
          const amt = row.drCr === 'Dr' ? parseAmt(row.debit) || 0 : parseAmt(row.credit) || 0;
          if (amt > 0) {
            entries.push({ ledger: row.ledger.value, type: row.drCr, amount: amt });
            if (row.drCr === 'Dr') totalDebit += amt; else totalCredit += amt;
          }
        }
      });
    } else if (data && data.party && data.party.value && data.items && data.items.length > 0) {
      const itemTotal   = parseAmt(TaxEngine.calculateItemsTotal(data.items)) || 0;
      const finalAmount = parseAmt(TaxEngine.calculateFinalAmount(data.items, data.charges || [])) || 0;

      if (itemTotal === 0) return { error: "Voucher cannot have zero item total." };

      let partyDrCr, mainDrCr, gstDrCr, discountDrCr, freightDrCr, roundOffPos, roundOffNeg;

      if (vType === 'Sales') {
        partyDrCr = 'Dr'; mainDrCr = 'Cr'; gstDrCr = 'Cr'; discountDrCr = 'Dr'; freightDrCr = 'Cr'; roundOffPos = 'Cr'; roundOffNeg = 'Dr';
      } else if (vType === 'Purchase') {
        partyDrCr = 'Cr'; mainDrCr = 'Dr'; gstDrCr = 'Dr'; discountDrCr = 'Cr'; freightDrCr = 'Dr'; roundOffPos = 'Dr'; roundOffNeg = 'Cr';
      } else if (vType === 'Credit Note') {
        partyDrCr = 'Cr'; mainDrCr = 'Dr'; gstDrCr = 'Dr'; discountDrCr = 'Cr'; freightDrCr = 'Dr'; roundOffPos = 'Dr'; roundOffNeg = 'Cr';
      } else if (vType === 'Debit Note') {
        partyDrCr = 'Dr'; mainDrCr = 'Cr'; gstDrCr = 'Cr'; discountDrCr = 'Dr'; freightDrCr = 'Cr'; roundOffPos = 'Cr'; roundOffNeg = 'Dr';
      }

      const isSalesType  = vType === 'Sales' || vType === 'Credit Note';
      const mainLedger   = isSalesType ? data.sales?.value : data.purchase?.value;
      if (!mainLedger) return { error: `Please select a ${isSalesType ? 'Sales' : 'Purchase'} Ledger.` };

      entries.push({ ledger: data.party.value, type: partyDrCr, amount: finalAmount });
      if (partyDrCr === 'Dr') totalDebit += finalAmount; else totalCredit += finalAmount;

      entries.push({ ledger: mainLedger, type: mainDrCr, amount: itemTotal });
      if (mainDrCr === 'Dr') totalDebit += itemTotal; else totalCredit += itemTotal;

      (data.charges || []).forEach(charge => {
        const ledgerName = charge.ledger?.value;
        const amount     = parseAmt(charge.amount) || 0;
        if (!ledgerName || amount === 0) return;

        let type     = '';
        let absAmount = Math.abs(amount);

        if (['CGST', 'SGST', 'IGST', 'CESS'].includes(ledgerName)) type = gstDrCr;
        else if (ledgerName === 'Discount')        type = discountDrCr;
        else if (ledgerName === 'Freight Charges') type = freightDrCr;
        else if (ledgerName === 'Round Off')       type = amount > 0 ? roundOffPos : roundOffNeg;
        else type = amount > 0 ? mainDrCr : partyDrCr;

        if (type) {
          entries.push({ ledger: ledgerName, type, amount: absAmount });
          if (type === 'Dr') totalDebit += absAmount; else totalCredit += absAmount;
        }
      });
    }

    if (totalDebit === 0 && totalCredit === 0) return { error: "Voucher cannot be blank." };
    if (Math.abs(totalDebit - totalCredit) > 0.005) {
      return { error: `Ledger imbalance detected: Dr (${totalDebit.toFixed(2)}) !== Cr (${totalCredit.toFixed(2)}).` };
    }

    return { entries, summary: { totalDebit, totalCredit } };
  }
};
